import type { ActiveFilter, FilterDefinition } from '@oysterjs/ui/search-filter';
import { BaseDateFilter, BaseSearchFilter, getSinceDate } from '~/api/data/filter-base';
import { type WorkflowExecution } from './executions-columns';
import { WorkflowExecutionState } from '~/api/graphql';
import { FunnelIcon } from '@heroicons/react/24/outline';
import type {
  FilterGeneratorContext,
  FilterSuggestion,
  SerializedFilter
} from '@oysterjs/ui/search-filter';

export type ExecutionFilter = 'search' | 'state' | 'date';

const STATE_OPTIONS: { value: WorkflowExecutionState; label: string }[] = [
  { value: WorkflowExecutionState.Queued, label: 'Queued' },
  { value: WorkflowExecutionState.Running, label: 'Running' },
  { value: WorkflowExecutionState.Completed, label: 'Completed' },
  { value: WorkflowExecutionState.Failed, label: 'Failed' },
  { value: WorkflowExecutionState.Cancelled, label: 'Cancelled' }
];

function executionMatchesFilter(
  exec: WorkflowExecution,
  filter: ActiveFilter<ExecutionFilter>
): boolean {
  switch (filter.type) {
    case 'search': {
      const term = (filter.value as string).toLowerCase();
      return exec.id.toLowerCase().includes(term);
    }
    case 'state':
      return exec.state === filter.value;
    case 'date': {
      const since = getSinceDate(filter.value as string);
      if (since == null) return true;
      return exec.createdAt != null && new Date(exec.createdAt) >= since;
    }
    default:
      return true;
  }
}

export function filterExecutions(
  executions: WorkflowExecution[],
  activeFilters: ActiveFilter<ExecutionFilter>[]
): WorkflowExecution[] {
  if (activeFilters.length === 0) return executions;
  return executions.filter((e) => activeFilters.every((f) => executionMatchesFilter(e, f)));
}

export class ExecutionStateFilter implements FilterDefinition<ExecutionFilter> {
  readonly type = 'state' as const;
  readonly icon = FunnelIcon;
  readonly priority = 10;
  readonly isUnique = false;

  generate({ query }: FilterGeneratorContext<ExecutionFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    return STATE_OPTIONS.filter(
      (o) => o.label.toLowerCase().includes(q) || 'state'.includes(q) || 'status'.includes(q)
    ).map((o) => ({
      value: o.value,
      displayLabel: 'State is',
      displayValue: o.label
    }));
  }

  deserialize(s: SerializedFilter<ExecutionFilter>): ActiveFilter<ExecutionFilter> {
    return {
      id: `state-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'State is',
      displayValue: s.displayValue
    };
  }
}

export class ExecutionDateFilter extends BaseDateFilter<ExecutionFilter> {
  constructor() {
    super('date');
  }
}

export class ExecutionSearchFilter extends BaseSearchFilter<ExecutionFilter> {
  constructor() {
    super('search');
  }
}

export const createExecutionFilterDefinitions = (): FilterDefinition<ExecutionFilter>[] => [
  new ExecutionStateFilter(),
  new ExecutionDateFilter(),
  new ExecutionSearchFilter()
];
